import React, { useMemo } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DevelopmentTierPanel, { TierAction } from '../../../../components/gamification/DevelopmentTierPanel';
import DevelopmentTierPanelLegacy from '../../../../components/gamification/DevelopmentTierPanelLegacy';
import useCapabilityRelease from '../../../../hooks/useCapabilityRelease';
import { adaptMedals } from '../../../../components/gamification/useLearnerGamification';
import useTierActions, { MEASURE, TierProgram } from '../home/member-learning-lifecycle/useTierActions';
import { activitySections, learnerActivity } from '../Program/components/learning-lifecycle/activityMeasures';
import type { IMemberProgram } from './MemberProgramsModal';
import type { ActivitySectionView } from '../../../../api/capability';
import { useMemberActivityHistory } from '../../../../queries/useCapability';
import { parseTierHistory } from '../../../../components/gamification/scorecardData';
import TeamActivitySections from '../Program/components/learning-lifecycle/TeamActivitySections';
import MemberPanelDialog from './MemberPanelDialog';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';

/**
 * One member's Development Tier, for an admin on Org Members — the learner's
 * DevelopmentTierPanel with its copy turned to the third person, fed the
 * member's medals from the Org Members payload and their outstanding items from
 * their Improve Performance queue. It carries no CTAs: nothing here can act on
 * the member's behalf, so the measures whose learner rows are buttons list
 * where the member stands instead.
 */

interface MemberDevelopmentTierModalProps {
  open: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
  /** members[].developmentTier from GET /getOrgMembersProgramProgress — the BE medals payload. */
  developmentTier: unknown;
  /** Capability release: their Roleplays and Practice Rounds sections (GET /v3/admin/capability/members). */
  capabilityActivity?: ActivitySectionView[];
  programs: IMemberProgram[];
}

// Org Members rows carry progress but not the learner dashboard's program
// status, so it's inferred. Only the queue targets (programs with unresolved
// lessons) matter here — the rows the hook builds from status are replaced below.
const toTierProgram = (p: IMemberProgram): TierProgram => ({
  id: p.id,
  name: p.name,
  // members[].programs are the member's mentee programs only.
  roles: [],
  status: p.progress >= 100 ? 'completed' : p.progress > 0 ? 'in-progress' : 'not-started',
  unresolvedLessons: p.unresolvedFlags ?? 0,
  locked: p.locked === true,
  resumeLesson: null
});

const MemberDevelopmentTierModal = ({
  open,
  onClose,
  memberId,
  memberName,
  developmentTier,
  programs,
  capabilityActivity
}: MemberDevelopmentTierModalProps) => {
  // With the Capability release (utils/capabilityRelease.ts): the member's
  // Activity measures and overall tier, worked out as on their own dashboard and
  // in the Members column, the average of the measures. Before it: the BE's
  // medals as they come, the overall tier being the lowest of five.
  const capabilityRelease = useCapabilityRelease();
  // A phone: the whole screen.
  const isMobileApp = useIsMobileApp();
  const TierPanel = capabilityRelease ? DevelopmentTierPanel : DevelopmentTierPanelLegacy;
  // With the release, Last 6 Months is their Activity % at the end of each week (the backend rebuilds it).
  const { data: activityHistory } = useMemberActivityHistory(capabilityRelease && open ? memberId : null);
  const medals = useMemo(() => {
    const be = adaptMedals(developmentTier);
    if (!capabilityRelease) return be;
    const { tier } = learnerActivity(be.sections, capabilityActivity);
    return {
      ...be,
      overall: tier ?? be.overall,
      sections: activitySections(be.sections, capabilityActivity, 'learner')
    };
  }, [developmentTier, capabilityActivity, capabilityRelease]);
  const tierPrograms = useMemo(() => programs.map(toTierProgram), [programs]);
  const { actionsByMeasure, loading } = useTierActions({ programs: tierPrograms, memberId });

  const actions = useMemo((): Record<string, TierAction[]> => {
    const queue = actionsByMeasure;
    // Same rows the learner sees, with their CTAs shown greyed out (no onClick):
    // the admin can see what the learner is being asked to do, not do it for them.
    return {
      ...queue,
      [MEASURE.lessonCompletion]: programs
        .filter((p) => p.progress > 0 && p.progress < 100)
        .map((p) => ({
          id: `complete-${p.id}`,
          programName: p.name,
          label: 'In progress',
          note: `${p.progress}%`,
          ctaLabel: 'Open This Lesson'
        })),
      // Locked programs can't be started, so they don't count against the member.
      [MEASURE.learningRange]: programs
        .filter((p) => p.progress <= 0 && !p.locked)
        .map((p) => ({ id: `start-${p.id}`, programName: p.name, label: '', ctaLabel: 'Open This Program' })),
      [MEASURE.proactiveLearning]: [
        { id: 'ask-elliott', programName: '', label: '', ctaLabel: 'Ask Elliott a Question' }
      ]
    };
  }, [actionsByMeasure, programs]);
  // With the release the admin sees where the member stands, without the buttons
  // only the member can press; a row that was nothing but its button goes.
  const shownActions = useMemo((): Record<string, TierAction[]> => {
    if (!capabilityRelease) return actions;
    return Object.fromEntries(
      Object.entries(actions).map(([measure, rows]) => [
        measure,
        rows.filter((row) => row.programName || row.label).map(({ ctaLabel, onClick, ...row }) => row)
      ])
    );
  }, [actions, capabilityRelease]);

  // With the release: exactly the learner dashboard's Activity tab (their level, Last 6 Months,
  // Activity Levels), in the third person.
  const activity = useMemo(
    () => learnerActivity(adaptMedals(developmentTier).sections, capabilityActivity),
    [developmentTier, capabilityActivity]
  );
  if (capabilityRelease) {
    return (
      <MemberPanelDialog open={open} onClose={onClose} flush title={`${memberName}'s Activity`}>
        <DevelopmentTierPanel
          embedded
          medals={medals}
          loading={false}
          actionsByMeasure={shownActions}
          actionsLoading={loading}
          memberName={memberName}
          intro={
            <TeamActivitySections
              activity={activity}
              history={activityHistory ? parseTierHistory(activityHistory.history) : undefined}
              audience="learner"
              memberName={memberName}
            />
          }
        />
      </MemberPanelDialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isMobileApp}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.60)', backdropFilter: 'blur(10.95px)' } }
      }}
      PaperProps={{
        sx: {
          width: 460,
          maxWidth: '95vw',
          height: 'min(820px, 90vh)',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#0F0F0F',
          borderRadius: '8px',
          outline: '1px solid #444444',
          outlineOffset: '-1px',
          overflow: 'hidden',
          boxShadow: 'none',
          [MOBILE_SX]: {
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            maxHeight: '100%',
            m: 0,
            borderRadius: 0,
            outline: 'none'
          }
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          pl: '25px',
          pr: '45px',
          py: '20px',
          borderBottom: '1px solid #2A2A2A',
          position: 'relative',
          [MOBILE_SX]: { pl: '16px', pr: '52px', py: '14px' }
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 18,
            fontWeight: 700,
            color: '#FDFDFD',
            fontFamily: 'Inter, sans-serif'
          }}
        >
          {memberName}&rsquo;s {capabilityRelease ? 'Activity' : 'Learning Tier'}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            p: 0.5,
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            [MOBILE_SX]: { p: '10px', right: 6 }
          }}
        >
          <CloseIcon sx={{ fontSize: 20, color: '#FDFDFD' }} />
        </IconButton>
      </Box>

      {/* The learner's panel, stretched to the modal instead of a dashboard column */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', p: '16px' }}>
        <TierPanel
          medals={medals}
          loading={false}
          actionsByMeasure={shownActions}
          actionsLoading={loading}
          memberName={memberName}
          sx={{ flex: 1, minWidth: 0, height: 'auto', mr: 0 }}
        />
      </Box>
    </Dialog>
  );
};

export default MemberDevelopmentTierModal;
